import React from 'react';
import { Stack, Typography } from '@mui/material';
import { FlowRun } from 'new/components/monitor/Runs/components/FlowRunsTable/types';
import { FlowRunStatus } from 'new/components/monitor/Runs/components/FlowRunStatus/FlowRunStatus';
import { FlowHealthBar } from 'new/elements/FlowHealthBar';
import FormatUtils from 'js/lib/FormatUtils';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import DateUtils from 'js/lib/DateUtils';
import styles from './FlowRunItem.module.scss';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';

interface FlowRunItemProps {
  run: FlowRun & { metrics?: any; last_run_id?: number; id?: number };
}

export const FlowRunItem: React.FC<FlowRunItemProps> = ({ run }) => {
  const runId = run.id || run.run_id;

  const formatRecords = () => {
    const hideRecords = shouldHideRecordsForResource(run);
    if (hideRecords) {
      const size = run?.metrics?.size || run?.size;
      return size ? FormatUtils.bytesToString(size) : '-';
    }

    return run?.metrics?.records
      ? `${FormatUtils.formatNamedNumber(run?.metrics?.records)} rec`
      : '-';
  };

  const formatErrors = () => {
    return run?.metrics?.errors
      ? `${FormatUtils.formatNamedNumber(run?.metrics?.errors)} err`
      : '-';
  };

  const formatTime = () => {
    const last_run_id = run?.last_run_id;
    if (!last_run_id || typeof last_run_id !== 'number') {
      return '-';
    }
    const lastRunDate = new Date(last_run_id);
    if (isNaN(lastRunDate.getTime())) {
      return '-';
    }

    return DateUtils.showTimeSince(last_run_id);
  };

  return (
    <div className={styles.flowRunItem}>
      <Stack direction="row" alignItems="stretch" gap="12px" sx={{ minWidth: 0 }}>
        <FlowHealthBar metrics={run?.metrics} />
        <Stack direction="column" gap="4px" sx={{ minWidth: 0 }}>
          <Typography variant="bodyDefault" className={styles.flowName}>
            {run.name}
          </Typography>
          <Stack direction="row" alignItems="center" gap="8px">
            <ChipWithCopy
              toCopy={runId}
              tooltipTitle="Click to copy Run ID"
              tooltipCopiedTitle="Run ID Copied"
            >
              ID {runId}
            </ChipWithCopy>
            <Typography variant="bodyDefault" className={styles.timestamp}>
              {formatTime()}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" gap="16px" className={styles.runMetrics}>
        <Stack direction="column" alignItems="flex-end">
          <Stack direction="row" alignItems="center" gap="8px">
            <Typography variant="bodySmall" className={styles.metricValue}>
              {formatRecords()}
            </Typography>
          </Stack>
          {run?.metrics?.errors > 0 && (
            <Stack direction="row" alignItems="center" gap="8px">
              <Typography variant="bodySmall" className={styles.errorValue}>
                {formatErrors()}
              </Typography>
            </Stack>
          )}
        </Stack>
        <div className={styles.statusSlot}>
          <FlowRunStatus row={run} />
        </div>
      </Stack>
    </div>
  );
};
